/**
 * Voucher transaction screens (sales import Find → edit → Save).
 * Outward / Inward keep legacy routes; others use /voucher/:slug.
 */

export function voucherSlugFromLabel(label) {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

/** @typedef {{
 *   slug: string,
 *   label: string,
 *   path: string,
 *   permission?: string,
 *   permissions?: string[],
 *   legacyScreen?: "billing",
 *   variant?: "outward" | "inward",
 *   partyGroup?: string,
 * }} VoucherPageConfig */

/** @type {VoucherPageConfig[]} */
export const VOUCHER_TRANSACTION_PAGES = [
  { slug: "credit-note", label: "Credit Note", path: "/voucher/credit-note", permission: "billing" },
  { slug: "credit-note-br", label: "Credit Note Br", path: "/voucher/credit-note-br", permission: "billing" },
  {
    slug: "credit-note-spareway",
    label: "Credit Note Spareway",
    path: "/voucher/credit-note-spareway",
    permission: "billing",
  },
  { slug: "credit-sales", label: "Credit Sales", path: "/voucher/credit-sales", permission: "billing" },
  { slug: "credit-sales-br", label: "Credit Sales Br", path: "/voucher/credit-sales-br", permission: "billing" },
  {
    slug: "credit-sales-spareway",
    label: "Credit Sales Spareway",
    path: "/voucher/credit-sales-spareway",
    permission: "billing",
  },
  { slug: "debit-note", label: "Debit Note", path: "/voucher/debit-note", permission: "billing" },
  {
    slug: "expenses-voucher",
    label: "Expenses Voucher",
    path: "/voucher/expenses-voucher",
    permission: "billing",
  },
  {
    slug: "inward-supply",
    label: "Inward Supply",
    path: "/voucher/inward-supply",
    permissions: ["billing", "purchase"],
    variant: "inward",
    partyGroup: "Sundry Creditors",
  },
  { slug: "material-order", label: "Material Order", path: "/voucher/material-order", permission: "billing" },
  {
    slug: "material-request",
    label: "Material Request",
    path: "/voucher/material-request",
    permission: "billing",
  },
  {
    slug: "outward-supply",
    label: "Outward Supply",
    path: "/billing",
    permission: "billing",
    legacyScreen: "billing",
  },
  {
    slug: "outward-supply-br",
    label: "Outward Supply Br",
    path: "/voucher/outward-supply-br",
    permission: "billing",
  },
  {
    slug: "outward-supply-spareway",
    label: "Outward Supply Spareway",
    path: "/voucher/outward-supply-spareway",
    permission: "billing",
  },
  {
    slug: "physical-stock",
    label: "Physical Stock",
    path: "/voucher/physical-stock",
    permission: "billing",
  },
  { slug: "qutation", label: "QUTATION", path: "/voucher/qutation", permission: "billing" },
  { slug: "transfer-in", label: "Transfer IN", path: "/voucher/transfer-in", permission: "billing" },
  {
    slug: "transfer-out-new",
    label: "Transfer Out - New",
    path: "/voucher/transfer-out-new",
    permission: "billing",
  },
];

const BY_SLUG = Object.fromEntries(VOUCHER_TRANSACTION_PAGES.map((p) => [p.slug, p]));
const BY_PATH = Object.fromEntries(
  VOUCHER_TRANSACTION_PAGES.flatMap((p) => {
    const entries = [[p.path, p]];
    if (p.slug === "inward-supply") entries.push(["/purchase", p]);
    return entries;
  })
);

/** Menu order under Transactions → Trading */
export const TRADING_VOUCHER_MENU_LABELS = VOUCHER_TRANSACTION_PAGES.map((p) => p.label);

export function getVoucherPageConfig(slugOrPath) {
  const key = String(slugOrPath || "").trim();
  if (!key) return null;
  return BY_SLUG[key] || BY_PATH[key] || BY_SLUG[voucherSlugFromLabel(key)] || null;
}

export function getVoucherPageConfigFromPathname(pathname) {
  let p = String(pathname || "").split("?")[0].trim();
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  if (p.startsWith("/voucher/")) {
    const slug = p.slice("/voucher/".length);
    return getVoucherPageConfig(slug);
  }
  return getVoucherPageConfig(p);
}

export const DEFAULT_OUTWARD_PAGE = getVoucherPageConfig("outward-supply");
export const DEFAULT_INWARD_PAGE = getVoucherPageConfig("inward-supply");

export function voucherPagePermissions(config) {
  if (!config) return ["billing"];
  if (config.permissions?.length) return config.permissions;
  return [config.permission || "billing"];
}
